import React, { useState,useEffect} from 'react';
import { ClipboardCheck, Plus, Filter, ChevronRight, Edit, MoreVertical, Trash2, Users, ChevronLeft, Search, X } from 'lucide-react';
import { Tag, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInterceptor';
import toast from 'react-hot-toast';

interface AssessmentsListProps {
  assessments: any[];
  filteredAssessments: any[];
  statusFilter: 'all' | 'active' | 'inactive' | 'draft';
  setStatusFilter: (filter: 'all' | 'active' | 'inactive' | 'draft') => void;
  openModal: () => void;
  handleClickAssessment: (assessment: any) => void;
  mockUserName: string;
  onAssessmentDeleted?: () => void;
  onNavigateToAssessment?: (assessmentId: string, sidebarItem: 'skills' | 'candidates') => void;
  isLoading?: boolean;
  hasMore?: boolean;
  currentPage?: number;
  totalPages?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

// Shimmer Skeleton Component
const AssessmentCardSkeleton = () => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col min-h-[300px] max-h-[500px] overflow-hidden relative">
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
      <div className="flex-1 relative z-10">
        <div className="flex flex-col items-start justify-between mb-4">
          <div className="flex items-center justify-between w-full mb-3">
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            <div className="flex items-center space-x-2">
              <div className="h-6 w-16 bg-gray-200 rounded"></div>
              <div className="h-5 w-5 bg-gray-200 rounded"></div>
            </div>
          </div>
          
          <div className="w-full space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="flex gap-2 mt-2">
              <div className="h-6 w-20 bg-gray-200 rounded"></div>
              <div className="h-6 w-24 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-4">
          <div className="h-6 w-16 bg-gray-200 rounded"></div>
          <div className="h-6 w-20 bg-gray-200 rounded"></div>
        </div>
      </div>
      
      <div className="flex justify-end items-center space-x-4 mt-4 pt-4 border-t border-gray-100 relative z-10">
        <div className="h-4 w-24 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
};

const assessmentStatus = (assessment: any) => {
  switch (assessment?.status) {
    case 'active':
      return  <Tag color="blue">{assessment?.status}</Tag>;
    case 'draft':
      return <Tag color="yellow">{assessment?.status}</Tag>;
    case 'inactive':
      return <Tag color="red">{assessment?.status}</Tag>;
    default:
      return <Tag color="gray">{assessment?.status}</Tag>;
  }
}

const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  return html.replace(/<\/?[^>]+(>|$)/g, '');
};

const AssessmentsList: React.FC<AssessmentsListProps> = ({
  assessments,
  filteredAssessments,
  statusFilter,
  setStatusFilter,
  openModal,
  handleClickAssessment,
  mockUserName,
  onAssessmentDeleted,
  onNavigateToAssessment,
  isLoading = false,
  hasMore = false,
  currentPage = 1,
  totalPages = 1,
  total = 0,
  onPageChange,
  searchQuery = '',
  onSearchChange,
}) => {
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (assessment: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setAssessmentToDelete(assessment);
    setDeleteModalVisible(true);
    setOpenMenuId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!assessmentToDelete) return;
    
    setDeleting(true);
    try {
      await axiosInstance.delete(`/assessments/${assessmentToDelete._id}`);
      toast.success('Assessment deleted successfully');
      setDeleteModalVisible(false);
      setAssessmentToDelete(null);
      if (onAssessmentDeleted) {
        onAssessmentDeleted();
      }
    } catch (error: any) {
      console.error('Error deleting assessment:', error);
      toast.error(error.response?.data?.message || 'Failed to delete assessment');
    } finally {
      setDeleting(false);
    }
  };

  const handleMenuToggle = (assessmentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === assessmentId ? null : assessmentId);
  };
  return (
    <div className="p-6">
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Hello, {mockUserName}! 👋
              </h1>
              <p className="text-lg text-gray-600">
                Welcome back! Here are your assessments.
              </p>
            </div>
            {/* Filter Dropdown */}
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive' | 'draft')}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700 text-sm font-medium cursor-pointer"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>
          
          {/* Search Box */}
          {onSearchChange && (
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search assessments by role, description, or domain..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Assessments Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Create Assessment Card - Always visible for all filters */}
            <div
              className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-6 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer flex flex-col items-center justify-center min-h-[200px]"
              onClick={openModal}
            >
              <div className="flex flex-col items-center justify-center text-center">
                <div className="bg-blue-100 rounded-full p-4 mb-4">
                  <Plus className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Create Assessment
                </h3>
                <p className="text-sm text-gray-600">
                  Add a new assessment to get started
                </p>
              </div>
            </div>
            {/* Shimmer Skeletons */}
            {[...Array(5)].map((_, index) => (
              <AssessmentCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        ) : filteredAssessments.length > 0 || assessments.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Create Assessment Card - Always visible for all filters */}
            <div
              className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-6 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer flex flex-col items-center justify-center min-h-[200px]"
              onClick={openModal}
            >
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="bg-blue-100 rounded-full p-4 mb-4">
                    <Plus className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Create Assessment
                  </h3>
                  <p className="text-sm text-gray-600">
                    Add a new assessment to get started
                  </p>
                </div>
              </div>

            {/* Assessment Cards */}
            {filteredAssessments.map((assessment: any) => (
              <div
                key={assessment._id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow flex flex-col min-h-[300px] max-h-[500px] overflow-hidden"
              >
                <div className="flex-1">
                  <div className="flex flex-col items-start justify-between mb-4">
                    <section className="flex items-center justify-between w-full">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {assessment.assessmentRole || 'Untitled Assessment'}
                      </h3>
                      <div className="flex items-center space-x-2">
                        {assessmentStatus(assessment)}
                        <div className="relative">
                          <button
                            onClick={(e) => handleMenuToggle(assessment._id, e)}
                            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                            aria-label="More options"
                          >
                            <MoreVertical className="h-5 w-5 text-gray-600" />
                          </button>
                          {openMenuId === assessment._id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setOpenMenuId(null)}
                              />
                              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onNavigateToAssessment) {
                                      onNavigateToAssessment(assessment._id, 'skills');
                                    } else {
                                      navigate(`/dashboard/assessment/${assessment._id}`);
                                      handleClickAssessment(assessment);
                                    }
                                  }}
                                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                  <Edit className="h-4 w-4" />
                                  <span>Edit</span>
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onNavigateToAssessment) {
                                      onNavigateToAssessment(assessment._id, 'candidates');
                                    } else {
                                      navigate(`/dashboard/assessment/${assessment._id}`);
                                      handleClickAssessment(assessment);
                                    }
                                  }}
                                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                  <Users className="h-4 w-4" />
                                  <span>Assign Candidates</span>
                                </button>
                                
                                <button
                                  onClick={(e) => handleDeleteClick(assessment, e)}
                                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  <span>Delete</span>
                                </button>
                                
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </section>
                  
                    <div className="flex-1 overflow-hidden">
                      {assessment.jobDescription && (
                        <p className="text-sm text-gray-600 line-clamp-4">
                          {stripHtmlTags(assessment.jobDescription)}
                        </p>
                      )}

                      {assessment.minExp!=null && assessment.maxExp!=null && (
                        <Tag color="gold" className='mt-2'>
                          {assessment.minExp} - {assessment.maxExp} years
                        </Tag>
                      )}

                        {assessment.minExp == 0 && (
                        <Tag color="green" className='mt-2'>
                         accepts freshers
                        </Tag>
                      )}
                      {assessment.duration && (
                        <Tag color="red" className='mt-2'>
                          {assessment.duration} mins
                        </Tag>
                      )}
                      {assessment.difficulty && (
                        <Tag color="yellow" className='mt-2'>
                          {assessment.difficulty}
                        </Tag>
                      )}

{assessment.skills?.length > 0 && (
  <div className="flex flex-wrap gap-2 mt-2">
    {assessment.skills.slice(0, 3).map((skill: any, index: number) => (
      <Tag color="blue" className="mt-2" key={index}>
        {skill?.name}
      </Tag>
    ))}

    {assessment.skills.length > 3 && (
      <span className="text-gray-500 text-xs mt-3">
        +{assessment.skills.length - 3}
      </span>
    )}
  </div>
)}

                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {assessment.domain && (
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {assessment.domain}
                      </span>
                    )}
                    {assessment.role && (
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                        {assessment.role}
                      </span>
                    )}
                    {assessment.isActive !== undefined && (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          assessment.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {assessment.isActive ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-end items-center space-x-4 mt-4 pt-4 border-t border-gray-100">
                  {/* <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/assessment/${assessment._id}`);
                      handleClickAssessment(assessment);
                    }}
                    className="flex items-center space-x-1 text-gray-600 text-sm font-medium hover:text-gray-900 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit</span>
                  </button> */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/assessment/${assessment._id}/interviews`);
                    }}
                    className="flex items-center space-x-1 text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors"
                  >
                    <span>View Candidates</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            
            {/* Pagination Controls */}
            {totalPages > 1 && onPageChange && total > 0 && (
              <div className="col-span-full flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * 20 + 1)} to {Math.min(currentPage * 20, total)} of {total} assessments
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1 || isLoading}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Previous</span>
                  </button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => onPageChange(pageNum)}
                          disabled={isLoading}
                          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || isLoading}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 transition-colors"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <ClipboardCheck className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {statusFilter === 'all' ? 'No Assessments' : `No ${statusFilter === 'active' ? 'Active' : 'Inactive'} Assessments`}
            </h3>
            <p className="text-gray-600 mb-6">
              {statusFilter === 'all' 
                ? 'You haven\'t created any assessments yet. Create your first assessment to get started.'
                : `No ${statusFilter === 'active' ? 'active' : 'inactive'} assessments found. Try changing the filter or create a new assessment.`
              }
            </p>
            <button
              onClick={openModal}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Create Assessment</span>
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Assessment"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalVisible(false);
          setAssessmentToDelete(null);
        }}
        confirmLoading={deleting}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete the assessment "{assessmentToDelete?.assessmentRole || 'Untitled Assessment'}"?
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default AssessmentsList;
